import React, { useMemo, useState } from "react";
import { selectClass } from "@/lib/formStyles";
import AvailabilityFormShell from "./AvailabilityFormShell";
import {
  DAYS_OF_WEEK,
  DEFAULT_GRID_LABEL,
  DayOfWeek,
  WeeklyAvailability,
  WeeklyAvailabilityRequest,
} from "../../services/availability/types";
import { buildTimeGrid, isTimeRangeValid } from "../../utils/availability";

/** Plage proposée à l'ajout : une matinée classique. */
const DEFAULT_START_TIME = "08:00";
const DEFAULT_END_TIME = "12:00";

type WeeklyRangeFormModalProps = {
  /** Plage modifiée ; absente = création. */
  range: WeeklyAvailability | null;
  /** Jour présélectionné à la création (bouton « + » d'une ligne). */
  defaultDay: DayOfWeek;
  slotMinutes: number;
  isSaving: boolean;
  onSubmit: (request: WeeklyAvailabilityRequest) => void;
  onDelete: (range: WeeklyAvailability) => void;
  onClose: () => void;
  /** Vrai quand la plage modifiée est la seule du médecin : la supprimer rouvre la grille par défaut. */
  isLastRange: boolean;
};

type ChoiceSelectProps = {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
};

const ChoiceSelect: React.FC<ChoiceSelectProps> = ({ label, value, options, onChange }) => (
  <label className="block text-sm font-medium text-slate-700">
    {label}
    <div className="relative">
      <select className={selectClass} value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <i className="bx bx-chevron-down absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-lg pointer-events-none" />
    </div>
  </label>
);

/** Ajout / modification d'une plage de la semaine type, bornes sur la grille. */
const WeeklyRangeFormModal: React.FC<WeeklyRangeFormModalProps> = ({
  range,
  defaultDay,
  slotMinutes,
  isSaving,
  onSubmit,
  onDelete,
  onClose,
  isLastRange,
}) => {
  const [dayOfWeek, setDayOfWeek] = useState<DayOfWeek>(range?.dayOfWeek ?? defaultDay);
  const [startTime, setStartTime] = useState(range?.startTime.slice(0, 5) ?? DEFAULT_START_TIME);
  const [endTime, setEndTime] = useState(range?.endTime.slice(0, 5) ?? DEFAULT_END_TIME);

  const timeOptions = useMemo(
    () => buildTimeGrid(slotMinutes).map((time) => ({ value: time, label: time })),
    [slotMinutes]
  );
  const isValid = isTimeRangeValid(startTime, endTime);

  return (
    <AvailabilityFormShell
      eyebrow="Semaine type"
      title={range ? "Modifier la plage" : "Nouvelle plage"}
      submitLabel={range ? "Enregistrer" : "Ajouter la plage"}
      canSubmit={isValid}
      isSaving={isSaving}
      onSubmit={() => onSubmit({ dayOfWeek, startTime, endTime })}
      onClose={onClose}
      onDelete={range ? () => onDelete(range) : undefined}
      deleteLabel="Supprimer cette plage"
    >
      <ChoiceSelect
        label="Jour"
        value={dayOfWeek}
        options={DAYS_OF_WEEK}
        onChange={(value) => setDayOfWeek(value as DayOfWeek)}
      />
      <div className="grid grid-cols-2 gap-4">
        <ChoiceSelect label="Début" value={startTime} options={timeOptions} onChange={setStartTime} />
        <ChoiceSelect label="Fin" value={endTime} options={timeOptions} onChange={setEndTime} />
      </div>
      {!isValid && <p className="text-xs text-rose-600">L'heure de fin doit suivre l'heure de début.</p>}
      {range && isLastRange && (
        <p className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <i className="bx bx-info-circle mt-0.5 text-sm" aria-hidden />
          <span>
            C'est la seule plage de la semaine type. La supprimer ne ferme aucune journée : les RDV seront
            proposés {DEFAULT_GRID_LABEL}. Pour ne plus recevoir de RDV, saisissez une absence.
          </span>
        </p>
      )}
    </AvailabilityFormShell>
  );
};

export default WeeklyRangeFormModal;
